
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

// Import our components
import Logo from "./navigation/Logo";
import UserMenu from "./navigation/UserMenu";
import MobileNavSheet from "./navigation/MobileNavSheet";
import MarketingNav from "./navigation/MarketingNav";
import { marketingItems, dashboardItems } from "./navigation/NavItems";

const Header = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  // Determine if we're on the marketing site (homepage)
  const isMarketingSite = location.pathname === "/";
  
  // ALWAYS hide the header on form routes
  const isFormRoute = location.pathname.startsWith('/form-preview/') || 
                      location.pathname.startsWith('/form-view/');
  
  // Determine if we should hide the header
  const shouldHideHeader = determineIfHeaderShouldBeHidden(isAuthenticated, isMarketingSite, location.pathname, isFormRoute);

  // Determine if we're on an auth page
  const isAuthPage = ["/login", "/register"].includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Don't show the header on auth pages or form routes
  if (isAuthPage || shouldHideHeader || isFormRoute) {
    console.log("Header not shown - isAuthPage:", isAuthPage, "shouldHideHeader:", shouldHideHeader, "isFormRoute:", isFormRoute);
    return null;
  }

  const currentNavItems = determineCurrentNavItems(isAuthenticated, isMarketingSite);
  const headerClasses = getHeaderClasses(isMarketingSite, isScrolled);

  return (
    <div className="flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={headerClasses}
      >
        <div className="container-padding h-[65px] flex items-center justify-between">
          <div className="flex items-center">
            <Logo isAuthenticated={isAuthenticated} />
          </div>

          {isMarketingSite ? (
            <div className="hidden md:flex flex-1 justify-center">
              <MarketingNav items={marketingItems} />
            </div>
          ) : null}

          <nav className="hidden md:flex items-center">
            <UserMenu 
              isAuthenticated={isAuthenticated} 
              logout={logout} 
              isMarketingSite={true}
              user={user}
              simplified={false}
            />
          </nav>

          <MobileNavSheet 
            navItems={currentNavItems} 
            isAuthenticated={isAuthenticated} 
            logout={logout} 
            simplified={false}
          />
        </div>
      </motion.header>
    </div>
  );
};

/**
 * Determines if the header should be hidden based on authentication and location
 */
const determineIfHeaderShouldBeHidden = (
  isAuthenticated: boolean, 
  isMarketingSite: boolean,
  pathname: string,
  isFormRoute: boolean
): boolean => {
  // Always hide header on form routes
  if (isFormRoute) {
    return true;
  }
  
  return isAuthenticated && 
    !isMarketingSite && 
    pathname !== "/pricing";
};

/**
 * Determines which navigation items to display
 */
const determineCurrentNavItems = (
  isAuthenticated: boolean,
  isMarketingSite: boolean
) => {
  return isAuthenticated && !isMarketingSite ? dashboardItems : marketingItems;
};

/**
 * Generates the appropriate header classes based on site section and scroll state
 */
const getHeaderClasses = (isMarketingSite: boolean, isScrolled: boolean): string => {
  if (isMarketingSite && !isScrolled) {
    return "fixed inset-x-0 top-0 z-50 transition-all duration-300 bg-transparent text-white";
  } else if (isMarketingSite) {
    return "fixed inset-x-0 top-0 z-50 transition-all duration-300 bg-[#0a0c1b]/80 backdrop-blur-lg text-white shadow-sm";
  } else if (isScrolled) {
    return "fixed inset-x-0 top-0 z-50 transition-all duration-300 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-sm";
  } else {
    return "fixed inset-x-0 top-0 z-50 transition-all duration-300 bg-transparent";
  }
};

export default Header;
